import { TextareaAutosize } from '@mui/base';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TelegramIcon from '@mui/icons-material/Telegram';
import { Avatar, Box, Typography, Stack, Tooltip, Button, Menu, MenuItem } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  useCreateCommentMutation,
  useDeleteCommentMutation,
  useLazyGetCommentsByIdQuery,
  useUpdateCommentMutation,
} from 'src/app/services/articlesCommentsApi';
import { useForm } from 'react-hook-form';
import { formatFullDate, formatSmartTime } from 'src/app/helpers/utils';
import ConfirmDeleteModalWindow from 'app/shared-components/confirmDeleteModalWindow';
import { useUser } from 'src/app/hooks/useUser';
import SimpleDialog from '@fuse/core/SimpleDialog';
import handleError from '@fuse/core/errorHandler';
import StaffContactDetails from 'app/shared-components/StaffContactDetails';
const CommentInput = ({
  refetchList,
  articleId,
  scrollToBottom,
  currentComment,
  setCurrentComment,
  width = false,
}) => {
  const { t } = useTranslation('translations');

  const [createComment, { isLoading: isCreating }] = useCreateCommentMutation();
  const [updateComment] = useUpdateCommentMutation();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      text: '',
    },
  });

  const textValue = watch('text');

  useEffect(() => {
    if (currentComment) {
      setValue('text', currentComment.text);
    } else {
      setValue('text', '');
    }
  }, [currentComment, setValue]);

  const isEdit = !!currentComment;

  const onSubmit = async (data) => {
    if (!data.text.trim()) {
      return;
    }
    try {
      if (isEdit) {
        await updateComment({
          id: currentComment.id,
          text: data.text,
        }).unwrap();
      } else {
        await createComment({
          articleId: articleId,
          parentCommentId: 0,
          text: data.text,
        }).unwrap();
      }

      await refetchList?.();
      scrollToBottom?.();
      setCurrentComment?.(null);
      reset({ text: '' });
    } catch (err) {
      handleError(err);
    }
  };

  return (
    <div
      className={`sticky bottom-0 bg-white dark:bg-darkblue border-t border-gray-200 px-16 py-7 z-10 ${
        width && 'w-full'
      }`}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex border rounded-2xl p-5 bg-gray/10 dark:bg-dark"
      >
        <div className="p-2 flex-1">
          <TextareaAutosize
            {...register('text', { required: true })}
            placeholder={t('WriteComment')}
            minRows={1}
            maxRows={7}
            className="w-full rounded resize-none bg-transparent text-black dark:text-white"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault(); 
                handleSubmit(onSubmit)();
              }
            }}
          />
        </div>
        <button
          type="submit"
          disabled={isCreating || !textValue.trim().length}
          className="p-7 self-end rounded-full hover:bg-darkgrey/5 disabled:opacity-50 flex items-center justify-center"
        >
          {isCreating ? (
            <div className="w-full h-full border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          ) : (
            <TelegramIcon className="text-5xl text-black dark:text-blue/90" />
          )}
        </button>
      </form>
    </div>
  );
};

const CommentMessage = ({ avatarUrl, name, message, timeAgo, id, refetchList, tbStaffId }) => {
  const { i18n, t } = useTranslation('translations');
  const lang = i18n.language;
  const [currentComment, setCurrentComment] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isDeleteCommentModalOpen, setIsDeleteCommentModalOpen] = useState(false);
  const [isEmplDetailsOpen, setIsEmplDetailsOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);

  const { user, isAdmin, isTop } = useUser();
  const [getById] = useLazyGetCommentsByIdQuery();
  const [deleteComment] = useDeleteCommentMutation();

  const open = Boolean(anchorEl);

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleCloseMenu();
    getById({ id })
      .then((response) => {
        setCurrentComment(response.data);
      })
      .catch((error) => {
        handleError(error);
      });
  };

  const handleDeleteComment = async () => {
    if (!commentToDelete) return;

    try {
      await deleteComment(id).unwrap();
      setIsDeleteCommentModalOpen(false);
      setCommentToDelete(null);
      handleCloseMenu();
      refetchList();
    } catch (error) {
      handleError(error);
    }
  };
  const handleDeleteCommentWithConfirm = (commentId) => {
    setCommentToDelete(commentId);
    setIsDeleteCommentModalOpen(true);
  };

  const handleEmplClick = () => {
    setIsEmplDetailsOpen(true);
    handleCloseMenu();
  };

  return (
    <>
      <Box display="flex" gap={1} py={1} className="group relative">
        <Avatar src={avatarUrl} sx={{ width: 35, height: 35 }} />
        {!currentComment ? (
          <Box flex={1}>
            <Box display="flex" gap={1}>
              <Box display="flex" gap={1}>
                <Box
                  bgcolor="#f0f2f5"
                  className="dark:bg-black pr-12 pt-5 pb-10 pl-7 rounded-t-16 rounded-br-16 rounded-bl-2 "
                >
                  <Typography
                    variant="body2"
                    fontSize={10}
                    fontWeight="500"
                    className="text-blue-500"
                  >
                    {name}
                  </Typography>
                  <Typography variant="body2" component="p" fontSize={12} className="leading-snug">
                    {message}
                  </Typography>
                </Box>

                {/* Tooltip + Icon Button */}
                <Tooltip title={t('More')}>
                  <button
                    onClick={handleOpenMenu}
                    className={`p-1 rounded hover:bg-gray-100/5 text-gray-600 self-center ${
                      open ? 'visible' : 'invisible group-hover:visible'
                    }`}
                  >
                    <MoreHorizIcon fontSize="small" />
                  </button>
                </Tooltip>
                {/* Dropdown menu */}
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleCloseMenu}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  {user.staffId == tbStaffId && (
                    <MenuItem sx={{ fontSize: '12px' }} onClick={handleEdit}>
                      {t('Edit')}
                    </MenuItem>
                  )}
                  {(user.staffId == tbStaffId || isAdmin || isTop) && (
                    <MenuItem
                      sx={{ fontSize: '12px' }}
                      onClick={() => handleDeleteCommentWithConfirm(id)}
                    >
                      {t('Delete')}
                    </MenuItem>
                  )}
                  {!(user.staffId == tbStaffId) && (
                    <MenuItem sx={{ fontSize: '12px' }} onClick={handleEmplClick}>
                      {t('Profile')}
                    </MenuItem>
                  )}
                </Menu>
              </Box>
            </Box>

            <Stack direction="row" spacing={2}>
              <Tooltip title={formatFullDate(timeAgo, lang)}>
                <Typography
                  ml={1}
                  color="text.secondary"
                  sx={{ fontSize: '10px', lineHeight: 1.2, marginTop: '5px' }}
                >
                  {formatSmartTime(timeAgo, lang)}
                </Typography>
              </Tooltip>
            </Stack>
          </Box>
        ) : (
          <div className="flex flex-col gap-1 items-start">
            <CommentInput
              width={true}
              currentComment={currentComment}
              setCurrentComment={setCurrentComment}
            />
            <button
              style={{
                fontSize: '12px',
                marginTop: '5px',
              }}
              onClick={() => setCurrentComment(false)}
            >
              {t('Cancel')}
            </button>
          </div>
        )}

        {isDeleteCommentModalOpen && (
          <ConfirmDeleteModalWindow
            onClose={() => {
              setIsDeleteCommentModalOpen(false);
            }}
            modalTitle={t('AreYouSureToDeleteComment')}
            deleteMessage={handleDeleteComment}
            open={isDeleteCommentModalOpen}
            setIsOpenModal={setIsDeleteCommentModalOpen}
          />
        )}
        <SimpleDialog
          open={isEmplDetailsOpen}
          onClose={() => setIsEmplDetailsOpen(false)}
          noPadding
        >
          <StaffContactDetails id={tbStaffId} />
        </SimpleDialog>
      </Box>
    </>
  );
};

export { CommentInput, CommentMessage };
